import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Radio,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import {
	useGetFamilyDetailQuery,
	useGetFamilyListQuery,
} from '@/api/family-management/familyApi';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { showLabel } from '@/utils/helper';
import { YESNO } from '@/common/constants/common';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import FamilyPairRelationshipDialog from './FamilyPairRelationshipDialog';
import { FamilyMergePlan, useFamilyMergePlan } from './useFamilyMergePlan';

interface Props {
	details: any;
}

const AddFamilyMemberToListDialog = (props: Props) => {
	const { details } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [plan, setPlan] = useState<FamilyMergePlan | null>(null);
	const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
	const { loadPlan, isLoading: isPlanLoading } = useFamilyMergePlan();
	const { data: selectDetails } = useGetFamilyDetailQuery(selectedRowId || '', {
		refetchOnMountOrArgChange: true,
		skip: !selectedRowId || !plan,
	});

	const defaultValues: DefaultValues<any> = {
		pagination: { ...initialPagination, size: 5 },
	};
	const methods = useForm<any>({
		defaultValues,
	});

	const watchSearchString = methods.watch('searchString');
	const watchPagination = methods.watch('pagination');

	// Reset to first page whenever the search string changes.
	useEffect(() => {
		methods.setValue('pagination', {
			...methods.getValues('pagination'),
			page: 1,
		});
	}, [watchSearchString]);

	useEffect(() => {
		if (open) {
			setSelectedRowId(null);
			methods.reset(defaultValues);
		}
	}, [open]);

	// CHA-006 §二: family members already in another family group are listed too.
	const { data: memberList, isFetching: isMemberListFetching } =
		useGetFamilyListQuery(
			{
				sortings: [],
				pagination: watchPagination,
				searchString: watchSearchString,
				excludeMember: details?.data.id,
				statusList: [0],
			},
			{
				refetchOnMountOrArgChange: true,
				skip: !open,
			}
		);

	const handleClose = () => {
		setOpen(false);
	};

	const onNext = async () => {
		if (!selectedRowId || !details?.data.id) return;
		const result = await loadPlan(details.data.id, selectedRowId);
		if (result) setPlan(result);
	};

	const columns: GridColDef[] = [
		{
			field: 'select',
			headerName: '',
			width: 60,
			renderCell: (params) => (
				<Radio
					checked={params.row.id === selectedRowId}
					onChange={() => setSelectedRowId(params.row.id)}
				/>
			),
		},
		{
			field: 'familyMemberFileNo',
			headerName: t('family.familyMemberFileNo'),
			flex: 15,
		},
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 15,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{
			field: 'phone',
			headerName: t('member.mobilePhone'),
			flex: 15,
		},
		{
			field: 'familyMemberStatus',
			headerName: t('member.status'),
			flex: 15,
			maxWidth: 140,
			minWidth: 140,
			renderCell: (params) => (
				<Chip
					color={
						params.row.familyMemberStatus == 1 ? 'default' : 'chipTertiary'
					}
					sx={{
						height: 'auto',
						minHeight: '24px',
						maxWidth: '100%',
						overflow: 'hidden',
						whiteSpace: 'normal',
						wordBreak: 'break-word',
						padding: '4px 4px',
					}}
					label={
						<Box
							sx={{
								width: '100%',
								whiteSpace: 'normal',
								wordBreak: 'break-word',
							}}
						>
							{params.row.familyMemberStatusDesc}
						</Box>
					}
				/>
			),
		},
	];

	return (
		<>
			<Button
				variant='outlined'
				endIcon={<GroupAddOutlinedIcon />}
				onClick={() => {
					setOpen(true);
				}}
				style={{ alignItems: 'center', marginLeft: '8px' }}
			>
				{t('family.addFamilyMemberToList')}
			</Button>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<DialogTitle variant='h5'>
					{t('family.linkExistingFamilyMember')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('family.linkExistingFamilyMemberText')}
					</Typography>
					<FormProvider {...methods}>
						<Grid
							container
							spacing={2}
							marginTop={0}
							marginBottom={2}
							alignItems='center'
						>
							<Grid size={12}>
								<AppSummarySearchBar
									name='searchString'
									control={methods.control}
									placeholder={t('common.search')}
								/>
							</Grid>
							<Grid size={12}>
								<AppDataGrid
									loading={isMemberListFetching}
									rows={memberList?.data.items || []}
									rowCount={memberList?.data.pagination.totalItems ?? 0}
									columns={columns}
									getRowId={(row) => row.id}
									control={methods.control}
								/>
							</Grid>
						</Grid>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						variant='contained'
						disabled={!selectedRowId || isPlanLoading}
						onClick={onNext}
					>
						{t('button.next')}
					</Button>
				</DialogActions>
			</Dialog>

			<FamilyPairRelationshipDialog
				open={!!plan}
				onClose={() => setPlan(null)}
				onSuccess={() => {
					setPlan(null);
					setSelectedRowId(null);
					setOpen(false);
				}}
				title={t('family.addFamilyMemberToList')}
				successMessage={t('snackbar.addFamilyMemberToListSuccess')}
				pairs={plan?.pairs || []}
				isMerge={!!plan?.isMerge}
				member={details?.data || {}}
			>
				<Typography variant='body1' sx={{ marginBottom: '12px' }}>
					{t('family.familyMemberInformationAddPerson')}
				</Typography>
				<Grid container spacing={3} marginTop={0} marginBottom={3}>
					<Grid size={6}>
						<AppFieldView
							label={t('member.name_en')}
							value={selectDetails?.data.nameEn}
						/>
					</Grid>
					<Grid size={6}>
						<AppFieldView
							label={t('member.name_zh')}
							value={selectDetails?.data.nameZh}
						/>
					</Grid>
					<Grid size={6}>
						<AppFieldView
							label={t('family.memberFileNo')}
							value={selectDetails?.data.fileNo}
						/>
					</Grid>
					<Grid size={6}>
						<AppFieldView
							label={t('family.familyMemberFileNo')}
							value={selectDetails?.data.familyMemberFileNo}
						/>
					</Grid>
					<Grid size={6}>
						<AppFieldView
							label={t('family.membershipRole')}
							value={selectDetails?.data.membershipRole}
						/>
					</Grid>
					<Grid size={6}>
						<AppFieldView
							label={t('member.isCase')}
							value={showLabel(selectDetails?.data.isCase, YESNO)}
						/>
					</Grid>
				</Grid>
			</FamilyPairRelationshipDialog>
		</>
	);
};

export default AddFamilyMemberToListDialog;
